import React, { useState, useEffect } from 'react';
import {
  FaStar, FaCheck, FaTimes, FaLightbulb,
  FaHistory, FaInfoCircle, FaClipboardCheck,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';

/*
   DESIGN TOKENS
*/
const C = {
  primary:   '#1B019B',
  primaryBg: '#ede9fe',
  success:   '#059669',
  successBg: '#ecfdf5',
  warning:   '#b45309',
  warningBg: '#fffbeb',
  danger:    '#e11d48',
  dangerBg:  '#fff1f2',
  amber:     '#d97706',
  amberBg:   '#fffbeb',
  gray:      '#64748b',
  grayBg:    '#f8fafc',
  border:    '#e2e8f0',
  white:     '#ffffff',
  ink:       '#0f172a',
  inkMid:    '#334155',
  inkLight:  '#64748b',
  inkFaint:  '#94a3b8',
};

/* 
   CSS GLOBAL (injecté une seule fois) - Sans polices personnalisées
 */
const GLOBAL_CSS = `
  .mep-page * { box-sizing: border-box; }
  .mep-page   { font-family: 'Segoe UI', 'Roboto', system-ui, -apple-system, sans-serif; color: ${C.ink}; }

  .mep-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 18px; border: none; border-radius: 10px;
    font-family: inherit; font-size: 13.5px; font-weight: 500;
    cursor: pointer; transition: all 0.18s ease; white-space: nowrap;
  }
  .mep-btn:hover    { filter: brightness(0.92); transform: translateY(-1px); }
  .mep-btn:active   { transform: translateY(0); }
  .mep-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; filter: none; }
  .mep-btn-primary  { background: ${C.primary}; color: #fff; }
  .mep-btn-ghost    { background: ${C.grayBg}; color: ${C.inkMid}; border: 1.5px solid ${C.border}; }
  .mep-btn-sm       { padding: 6px 14px; font-size: 13px; border-radius: 8px; }

  .mep-input {
    width: 100%; padding: 9px 13px;
    border: 1.5px solid ${C.border}; border-radius: 10px;
    font-family: inherit; font-size: 14px;
    outline: none; background: ${C.white}; color: ${C.ink};
    transition: border-color 0.15s;
  }
  .mep-input:focus       { border-color: ${C.primary}; }
  .mep-input::placeholder { color: ${C.inkFaint}; }

  .mep-card {
    background: ${C.white}; border-radius: 16px;
    border: 1px solid ${C.border};
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    overflow: hidden;
  }

  .mep-tab {
    padding: 10px 20px; border: none; cursor: pointer;
    font-family: inherit; font-size: 13.5px; font-weight: 500;
    color: ${C.inkLight}; background: none;
    border-bottom: 2.5px solid transparent;
    display: inline-flex; align-items: center; gap: 8px;
    transition: all 0.15s; white-space: nowrap;
  }
  .mep-tab:hover  { color: ${C.ink}; }
  .mep-tab.active { color: ${C.primary}; border-bottom-color: ${C.primary}; }

  .mep-inn-card {
    background: ${C.white}; border: 1.5px solid ${C.border};
    border-radius: 14px; padding: 22px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .mep-inn-card:hover      { box-shadow: 0 6px 20px rgba(27,1,155,0.09); border-color: #c4b5fd; }
  .mep-inn-card.mep-done   { opacity: 0.75; background: ${C.grayBg}; }
  .mep-inn-card.mep-done:hover { box-shadow: none; border-color: ${C.border}; }

  .mep-overlay {
    position: fixed; inset: 0; background: rgba(15,23,42,0.52);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px; backdrop-filter: blur(3px);
    animation: mepFade 0.15s ease;
  }
  .mep-modal {
    background: ${C.white}; border-radius: 20px;
    width: 100%; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 24px 80px rgba(0,0,0,0.18);
    animation: mepSlide 0.2s ease;
  }
  @keyframes mepFade  { from { opacity: 0 } to { opacity: 1 } }
  @keyframes mepSlide { from { transform: translateY(14px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }

  .mep-note-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 11px 14px; border-radius: 10px; background: ${C.grayBg};
    margin-bottom: 8px; gap: 12px;
  }
  .mep-note-input {
    width: 82px; padding: 7px 10px; text-align: center;
    border: 1.5px solid ${C.border}; border-radius: 8px;
    font-family: inherit; font-size: 14px; font-weight: 600;
    color: ${C.primary}; outline: none; flex-shrink: 0; background: ${C.white};
    transition: border-color 0.15s;
  }
  .mep-note-input:focus         { border-color: ${C.primary}; }
  .mep-note-input.mep-note-over { border-color: ${C.danger}; color: ${C.danger}; }

  .mep-score-bar      { height: 5px; border-radius: 3px; background: ${C.border}; overflow: hidden; }
  .mep-score-bar-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, ${C.primary}, #7c3aed); transition: width 0.4s ease; }

  .mep-spinner {
    width: 30px; height: 30px; border-radius: 50%;
    border: 3px solid ${C.border}; border-top-color: ${C.primary};
    animation: mepSpin 0.7s linear infinite; margin: 50px auto; display: block;
  }
  .mep-spin-inline {
    width: 14px; height: 14px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff;
    display: inline-block; animation: mepSpin 0.7s linear infinite; flex-shrink: 0;
  }
  @keyframes mepSpin { to { transform: rotate(360deg) } }

  .mep-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 12px; padding: 56px 24px; color: ${C.gray};
  }
  .mep-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 11.5px; font-weight: 600;
  }
`;

const injectCSS = () => {
  if (document.getElementById('mep-styles')) return;
  const el = document.createElement('style');
  el.id = 'mep-styles';
  el.textContent = GLOBAL_CSS;
  document.head.appendChild(el);
};

/* 
   COMPOSANT — MODALE GÉNÉRIQUE
 */
const Modal = ({ show, onClose, title, subtitle, children, footer, maxWidth = 560 }) => {
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape' && show) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [show, onClose]);

  if (!show) return null;
  return (
    <div className="mep-overlay" onClick={onClose}>
      <div className="mep-modal" style={{ maxWidth }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{title}</h3>
              {subtitle && <p style={{ margin: '4px 0 0', fontSize: 13, color: C.inkLight }}>{subtitle}</p>}
            </div>
            <button onClick={onClose} className="mep-btn mep-btn-ghost mep-btn-sm" style={{ padding: '7px 10px', marginLeft: 12 }}>
              <FaTimes style={{ fontSize: 13 }} />
            </button>
          </div>
        </div>
        <div style={{ padding: '22px 26px' }}>{children}</div>
        {footer && (
          <div style={{
            padding: '16px 26px', borderTop: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            background: C.grayBg, borderRadius: '0 0 20px 20px',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/*
   COMPOSANT — BADGE STATUT
 */
const StatutBadge = ({ statut, label }) => {
  const MAP = {
    soumise:       { bg: '#eff6ff', color: '#2563eb' },
    en_evaluation: { bg: C.amberBg,   color: C.warning  },
    validee:       { bg: C.successBg, color: C.success  },
    rejetee:       { bg: C.dangerBg,  color: C.danger   },
  };
  const s = MAP[statut] || { bg: '#f1f5f9', color: C.gray };
  return (
    <span className="mep-badge" style={{ background: s.bg, color: s.color }}>
      {label}
    </span>
  );
};

/*
   COMPOSANT — CARTE INNOVATION
 */
const InnovationCard = ({ inn, onEvaluer, done = false }) => (
  <div className={`mep-inn-card${done ? ' mep-done' : ''}`}>
    {/* Titre + badge */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.35, marginBottom: 6 }}>
          {inn.titre}
        </div>
        {inn.domaine && (
          <span style={{
            fontSize: 12, background: C.primaryBg, color: C.primary,
            padding: '2px 9px', borderRadius: 20, fontWeight: 500,
          }}>
            {inn.domaine}
          </span>
        )}
      </div>
      {done ? (
        <span style={{
          background: C.successBg, color: C.success, padding: '4px 10px',
          borderRadius: 20, fontSize: 12, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
        }}>
          <FaCheck style={{ fontSize: 10 }} /> Soumise
        </span>
      ) : (
        <span style={{
          background: C.amberBg, color: C.warning,
          padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          À évaluer
        </span>
      )}
    </div>

    {/* Grille d'infos */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
      {[
        { label: 'Auteur',          value: `${inn.auteur_prenom || ''} ${inn.auteur_nom || ''}`.trim() || '—' },
        { label: 'Organisation',    value: inn.organisation },
        { label: 'Date soumission', value: inn.date_soumission },
        { label: 'Scores reçus',    value: `${inn.nombre_evaluateurs} évaluateur(s)` },
      ].map(({ label, value }, i) => (
        <div key={i} style={{ background: C.grayBg, borderRadius: 8, padding: '8px 11px' }}>
          <div style={{ fontSize: 10.5, color: C.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
            {label}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.inkMid }}>{value || '—'}</div>
        </div>
      ))}
    </div>

    {/* Bouton ou confirmation */}
    {!done ? (
      <button
        className="mep-btn mep-btn-primary"
        style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
        onClick={onEvaluer}
      >
        <FaStar style={{ fontSize: 13 }} /> Évaluer cette innovation
      </button>
    ) : (
      <div style={{ textAlign: 'center', fontSize: 13, color: C.success, padding: '6px 0 2px', fontWeight: 500 }}>
        <FaClipboardCheck style={{ marginRight: 6 }} />
        Votre évaluation a bien été enregistrée
      </div>
    )}
  </div>
);

/* 
   PAGE PRINCIPALE
*/
const MesEvaluationsPage = () => {
  const axiosInstance = useAxios();

  const [activeTab,  setActiveTab]  = useState('aEvaluer');
  const [aEvaluer,   setAEvaluer]   = useState([]);
  const [historique, setHistorique] = useState([]);
  const [loading,    setLoading]    = useState(false);

  /* Modale évaluation */
  const [showModal,   setShowModal]   = useState(false);
  const [innActive,   setInnActive]   = useState(null);
  const [criteres,    setCriteres]    = useState([]);
  const [notes,       setNotes]       = useState([]);
  const [commentaire, setCommentaire] = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  /* ── Init ── */
  useEffect(() => { injectCSS(); }, []);
  useEffect(() => { fetchAEvaluer(); fetchHistorique(); }, []);

  /* ── Fetch innovations assignées ── */
  const fetchAEvaluer = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/innovation/innovations/mes-evaluations/');
      setAEvaluer(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) {
      console.error(e);
      setAEvaluer([]);
    } finally {
      setLoading(false);
    }
  };

  /* ── Fetch historique scores ── */
  const fetchHistorique = async () => {
    try {
      const res = await axiosInstance.get('/innovation/innovations/mes-evaluations-soumises/');
      setHistorique(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) {
      console.error(e);
      setHistorique([]);
    }
  };

  /* ── Ouvrir modale avec critères de l'organisation ── */
  const ouvrirEvaluation = async (inn) => {
    try {
      const res  = await axiosInstance.get('/innovation/innovations/criteres/', {
        params: { organisation: inn.organisation_id },
      });
      const crits = (Array.isArray(res.data) ? res.data : []).filter(c => c.est_actif);

      setCriteres(crits);
      setInnActive(inn);
      setNotes(crits.map(c => ({ critere_id: c.id, note: '' })));
      setCommentaire('');
      setShowModal(true);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: "Impossible de charger les critères d'évaluation." });
    }
  };

  /* ── Soumettre l'évaluation ── */
  const handleSoumettre = async () => {
    // Toutes les notes renseignées ?
    const manquants = notes.filter(n => n.note === '' || n.note === null || n.note === undefined);
    if (manquants.length > 0) {
      return Swal.fire({ icon: 'error', title: 'Notes incomplètes', text: 'Veuillez noter tous les critères avant de soumettre.' });
    }

    // Aucune note ne dépasse le max ?
    const horsMax = notes.find(n => {
      const c = criteres.find(c => c.id === n.critere_id);
      return c && Number(n.note) > c.note_max;
    });
    if (horsMax) {
      const c = criteres.find(c => c.id === horsMax.critere_id);
      return Swal.fire({ icon: 'error', title: 'Note invalide', text: `La note pour "${c?.nom}" dépasse le maximum autorisé (${c?.note_max}).` });
    }

    const confirm = await Swal.fire({
      title: "Confirmer l'évaluation ?",
      html: `<span style="color:${C.inkLight};font-size:14px">
        Cette action est <strong>définitive</strong>. Votre score ne pourra plus être modifié.
      </span>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: C.primary,
      cancelButtonColor: C.gray,
      confirmButtonText: 'Oui, soumettre',
      cancelButtonText: 'Annuler',
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await axiosInstance.post(
        `/innovation/innovations/${innActive.id}/soumettre-evaluation/`,
        {
          notes: notes.map(n => ({ critere_id: n.critere_id, note: Number(n.note) })),
          commentaire,
        }
      );
      Swal.fire({
        icon: 'success',
        title: 'Évaluation soumise !',
        html: `<div style="text-align:center;font-size:15px">
          Score total : <strong style="color:${C.primary};font-size:22px">${res.data.score_total} pts</strong>
        </div>`,
        timer: 2800,
        showConfirmButton: false,
      });
      setShowModal(false);
      fetchAEvaluer();
      fetchHistorique();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Helpers notes ── */
  const setNote    = (critere_id, val) =>
    setNotes(prev => prev.map(n => n.critere_id === critere_id ? { ...n, note: val } : n));
  const getNoteVal = critere_id =>
    notes.find(n => n.critere_id === critere_id)?.note ?? '';

  /* ── Score estimé ── */
  const scoreEstime = notes.reduce((s, n) => s + Number(n.note || 0), 0);
  const scoreMax    = criteres.reduce((s, c) => s + c.note_max, 0);
  const pctEstime   = scoreMax > 0 ? Math.min(100, Math.round((scoreEstime / scoreMax) * 100)) : 0;

  const enAttente = aEvaluer.filter(i => !i.deja_evalue);
  const dejaFait  = aEvaluer.filter(i => i.deja_evalue);

  /* 
     RENDU
  */
  return (
    <div
      className="mep-page"
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        padding: '28px 32px',
      }}
    >
      {/* ── En-tête ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
          <span style={{ fontSize: 22, color: C.amber }}><FaStar /></span>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
            Mes évaluations d'innovation
          </h1>
        </div>
        <p style={{ margin: 0, color: C.inkLight, fontSize: 14 }}>
          Innovations pour lesquelles vous avez été désigné évaluateur par votre responsable RH
        </p>
      </div>

      {/* ── Cartes stats ── */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 26, flexWrap: 'wrap' }}>
        {[
          { label: 'En attente',    value: enAttente.length,  color: C.primary, bg: C.primaryBg },
          { label: 'Déjà évaluées', value: dejaFait.length,   color: C.success, bg: C.successBg },
          { label: 'Total reçues',  value: aEvaluer.length,   color: C.inkMid,  bg: C.grayBg    },
          { label: 'Scores soumis', value: historique.length, color: C.warning, bg: C.amberBg   },
        ].map((s, i) => (
          <div key={i} style={{
            background: s.bg, borderRadius: 12, padding: '14px 22px',
            border: `1px solid ${C.border}`, minWidth: 148, flex: '1 1 148px',
          }}>
            <div style={{ fontSize: 11.5, color: s.color, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {s.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Onglets ── */}
      <div style={{ borderBottom: `2px solid ${C.border}`, marginBottom: 24, display: 'flex', gap: 2 }}>
        {[
          { key: 'aEvaluer',  label: 'À évaluer',  icon: <FaLightbulb />, count: enAttente.length },
          { key: 'historique',label: 'Mes scores',  icon: <FaHistory /> },
        ].map(t => (
          <button
            key={t.key}
            className={`mep-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.icon}
            {t.label}
            {t.count > 0 && (
              <span style={{
                background: C.primary, color: '#fff',
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 
          ONGLET À ÉVALUER
      */}
      {activeTab === 'aEvaluer' && (
        <>
          {loading ? (
            <div className="mep-spinner" />
          ) : aEvaluer.length === 0 ? (
            <div className="mep-card">
              <div className="mep-empty">
                <FaLightbulb style={{ fontSize: 38, color: C.border }} />
                <p style={{ margin: 0, fontSize: 15 }}>Aucune innovation à évaluer pour le moment</p>
                <p style={{ margin: 0, fontSize: 13, color: C.gray }}>
                  Votre responsable RH vous désignera lorsqu'une évaluation vous sera assignée
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Sous-section : en attente */}
              {enAttente.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: C.inkMid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      En attente
                    </h3>
                    <span style={{ background: C.primaryBg, color: C.primary, borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>
                      {enAttente.length}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 16 }}>
                    {enAttente.map(inn => (
                      <InnovationCard key={inn.id} inn={inn} onEvaluer={() => ouvrirEvaluation(inn)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Sous-section : déjà évaluées */}
              {dejaFait.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: C.inkLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Déjà évaluées
                    </h3>
                    <span style={{ background: C.successBg, color: C.success, borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>
                      {dejaFait.length}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 16 }}>
                    {dejaFait.map(inn => (
                      <InnovationCard key={inn.id} inn={inn} done />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* 
          ONGLET HISTORIQUE
      */}
      {activeTab === 'historique' && (
        <div className="mep-card">
          {historique.length === 0 ? (
            <div className="mep-empty">
              <FaHistory style={{ fontSize: 38, color: C.border }} />
              <p style={{ margin: 0, fontSize: 15 }}>Aucun score soumis pour le moment</p>
            </div>
          ) : (
            historique.map((s, i) => {
              const totalMax = s.notes.reduce((acc, n) => acc + n.note_max, 0);
              const pct      = totalMax > 0 ? Math.round((s.score_total / totalMax) * 100) : 0;
              return (
                <div key={s.id} style={{
                  padding: '20px 24px',
                  borderBottom: i < historique.length - 1 ? `1px solid ${C.border}` : 'none',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>

                    {/* Infos */}
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 15 }}>{s.innovation_titre}</strong>
                        <StatutBadge statut={s.innovation_statut} label={s.innovation_statut_display} />
                      </div>
                      <div style={{ fontSize: 12.5, color: C.inkFaint, marginBottom: 12 }}>
                        Soumis le {s.date}
                      </div>
                      {/* Notes par critère */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: s.commentaire ? 10 : 0 }}>
                        {s.notes.map((n, j) => (
                          <span key={j} style={{
                            background: C.grayBg, border: `1px solid ${C.border}`,
                            borderRadius: 8, padding: '4px 10px', fontSize: 12.5,
                          }}>
                            {n.critere_nom} : <strong style={{ color: C.primary }}>{n.note}/{n.note_max}</strong>
                          </span>
                        ))}
                      </div>
                      {s.commentaire && (
                        <p style={{ margin: 0, fontSize: 13, color: C.inkLight, fontStyle: 'italic' }}>
                          "{s.commentaire}"
                        </p>
                      )}
                    </div>

                    {/* Score total */}
                    <div style={{ textAlign: 'center', minWidth: 90 }}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: C.primary, lineHeight: 1 }}>
                        {s.score_total ?? '—'}
                      </div>
                      <div style={{ fontSize: 11.5, color: C.inkFaint, marginTop: 2 }}>
                        / {totalMax} pts
                      </div>
                      {s.score_total != null && totalMax > 0 && (
                        <>
                          <div className="mep-score-bar" style={{ width: 88, margin: '8px auto 0' }}>
                            <div className="mep-score-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div style={{ fontSize: 11.5, color: C.inkLight, marginTop: 4 }}>{pct}%</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <Modal
        show={showModal}
        onClose={() => { if (!submitting) setShowModal(false); }}
        title="Saisir votre évaluation"
        subtitle={innActive?.titre}
        maxWidth={540}
        footer={
          <>
            <button
              className="mep-btn mep-btn-ghost"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              className="mep-btn mep-btn-primary"
              onClick={handleSoumettre}
              disabled={submitting}
            >
              {submitting
                ? <><span className="mep-spin-inline" /> Envoi en cours...</>
                : <><FaCheck /> Soumettre mon évaluation</>
              }
            </button>
          </>
        }
      >
        {/* Bloc info innovation */}
        {innActive && (
          <div style={{
            background: C.primaryBg, border: '1px solid #c4b5fd',
            borderRadius: 12, padding: '14px 16px', marginBottom: 22,
          }}>
            <div style={{ fontSize: 11.5, color: C.primary, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Innovation à évaluer
            </div>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{innActive.titre}</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: C.inkLight }}>
              {innActive.domaine && (
                <span>Domaine : <strong style={{ color: C.inkMid }}>{innActive.domaine}</strong></span>
              )}
              <span>
                Auteur : <strong style={{ color: C.inkMid }}>
                  {`${innActive.auteur_prenom || ''} ${innActive.auteur_nom || ''}`.trim() || '—'}
                </strong>
              </span>
            </div>
          </div>
        )}

        {/* Critères ou message vide */}
        {criteres.length === 0 ? (
          <div style={{
            background: C.amberBg, border: '1px solid #fde68a',
            borderRadius: 10, padding: '14px 16px',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <FaInfoCircle style={{ color: C.warning, flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: 14, color: C.warning }}>
              Aucun critère d'évaluation actif configuré. Contactez votre responsable RH.
            </span>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.inkMid, marginBottom: 12, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
              Notes par critère <span style={{ color: C.danger }}>*</span>
            </div>

            {criteres.map(c => {
              const val    = getNoteVal(c.id);
              const isOver = val !== '' && Number(val) > c.note_max;
              return (
                <div key={c.id} className="mep-note-row">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nom}</div>
                    <div style={{ fontSize: 12, color: C.inkFaint }}>sur {c.note_max} points</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={c.note_max}
                    step="0.5"
                    className={`mep-note-input${isOver ? ' mep-note-over' : ''}`}
                    value={val}
                    onChange={e => setNote(c.id, e.target.value)}
                    placeholder="0"
                  />
                </div>
              );
            })}

            {/* Score estimé */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: C.primaryBg, borderRadius: 10, marginTop: 6,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.primary, marginBottom: 6 }}>Score estimé</div>
                <div className="mep-score-bar" style={{ width: 130 }}>
                  <div className="mep-score-bar-fill" style={{ width: `${pctEstime}%` }} />
                </div>
                <div style={{ fontSize: 11.5, color: C.inkLight, marginTop: 4 }}>{pctEstime}%</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 26, fontWeight: 700, color: C.primary }}>
                  {scoreEstime}
                </span>
                <span style={{ fontSize: 13, color: C.inkLight }}> / {scoreMax}</span>
              </div>
            </div>
          </>
        )}

        {/* Commentaire */}
        <div style={{ marginTop: 18 }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.inkMid, marginBottom: 6 }}>
            Commentaire{' '}
            <span style={{ color: C.inkFaint, fontWeight: 400 }}>(optionnel)</span>
          </label>
          <textarea
            className="mep-input"
            rows={3}
            style={{ resize: 'vertical', lineHeight: 1.65 }}
            placeholder="Partagez vos observations sur cette innovation..."
            value={commentaire}
            onChange={e => setCommentaire(e.target.value)}
          />
        </div>

        {/* Avertissement */}
        <div style={{
          marginTop: 14, display: 'flex', gap: 9, alignItems: 'flex-start',
          padding: '11px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
        }}>
          <FaInfoCircle style={{ color: '#ea580c', flexShrink: 0, marginTop: 1, fontSize: 14 }} />
          <span style={{ fontSize: 13, color: '#9a3412', lineHeight: 1.5 }}>
            Une fois soumise, votre évaluation est <strong>définitive</strong> et ne pourra plus être modifiée.
          </span>
        </div>
      </Modal>
    </div>
  );
};

export default MesEvaluationsPage;